// ScalapayWidget.tsx
//
// Thin wrapper around Scalapay's own <scalapay-widget> custom element (the
// "pay in installments" promo widget).
//
// The provider's loader script is injected from a client effect below rather
// than server-rendered into <head>. That is deliberate and load-bearing: the
// loader injects its own nodes into <head> (its widget bundle, and a Google
// Fonts stylesheet), and when it was a server-rendered <script> in the document
// head it ran before React finished hydrating, so React found a <head> that no
// longer matched what it had rendered. The result was "Hydration failed …
// the entire root will switch to client rendering" on EVERY page of the site
// whenever Scalapay was enabled — not just pages showing a widget. Injecting
// after mount cannot race hydration, and has the side benefit of only pulling
// the script on pages that actually render a widget.
//
// Per Scalapay's integration guideline this is a PURELY informational/
// graphic element — its ONLY interactive part is the info modal Scalapay's
// own script wires up internally. Never wrap it in a button/link, never
// attach click handlers, never use it as a payment-method selector.

import { useEffect } from "react";
import type { DetailedHTMLProps, HTMLAttributes } from "react";

export type ScalapayWidgetPlacement = "product" | "cart" | "checkout";

const LOADER_SRC =
  "https://cdn.scalapay.com/widget/scalapay-widget-loader.js?version=V5";
// Marks our injected tag so repeated mounts (several placements on one page,
// client-side navigation) reuse the single script instead of adding more.
const LOADER_MARKER = "data-scalapay-loader";

/**
 * Injects Scalapay's widget loader once per document, after hydration.
 * No-op on the server and on every mount after the first.
 */
function useScalapayLoaderScript(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    if (document.querySelector(`script[${LOADER_MARKER}]`)) return;
    const script = document.createElement("script");
    script.type = "module";
    script.src = LOADER_SRC;
    script.async = true;
    script.setAttribute(LOADER_MARKER, "true");
    document.head.appendChild(script);
    // Deliberately not removed on unmount: the custom element it defines is
    // registered document-wide and other placements may still need it.
  }, [enabled]);
}

// Declares the custom element for JSX/TSX so it can be used without an `any`
// cast. Module augmentation of the global JSX namespace (this file is a
// module — it has top-level imports/exports — so `declare global` applies
// project-wide, matching how @types/react@18.2 exposes JSX.IntrinsicElements
// under "jsx": "react-jsx").
declare global {
  // Augmenting the ambient global JSX namespace is the only way to add a
  // custom-element intrinsic in this @types/react@18.2 + "jsx": "react-jsx"
  // setup — there is no ES2015-module equivalent for this specific case.
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "scalapay-widget": DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & {
        "merchant-token"?: string;
        amount?: string;
        "min-amount"?: string;
        "max-amount"?: string;
        type?: ScalapayWidgetPlacement;
        locale?: string;
        environment?: string;
        channel?: string;
      };
    }
  }
}

export interface ScalapayWidgetProps {
  /**
   * Public Scalapay merchant token. Nothing renders when this is missing —
   * callers are expected to already fold SCALAPAY_ENABLED into whether they
   * pass a token at all (see the SCALAPAY_MERCHANT_TOKEN field surfaced by
   * app/root.tsx's loader, which is `undefined` whenever the flag is off).
   */
  merchantToken?: string | null;
  /** Amount (major currency units, e.g. euros) the widget should quote. */
  amount: number;
  /** Eligibility window, from SCALAPAY_MIN_AMOUNT / SCALAPAY_MAX_AMOUNT. */
  minAmount: number;
  maxAmount: number;
  /** Widget placement, per Scalapay's best-practice guideline. */
  type: ScalapayWidgetPlacement;
  /**
   * Scalapay's industry channel. Their widget accepts exactly one value,
   * "travel" (verified against the v5 bundle, whose channel enum has that
   * single member), and it swaps the copy in the info modal for
   * travel-specific wording.
   *
   * Set on the booking payment step (requested 26 Aug 2026), on the shop
   * product page and cart (28 Aug 2026) and, since 31 Aug 2026, on both
   * shop checkout widgets as well, so the customer sees the same travel
   * wording from the product page through to payment.
   */
  channel?: "travel";
  /**
   * True when SCALAPAY_API_BASE_URL points at the sandbox host — surfaced by
   * the caller from env (see app/root.tsx), never guessed client-side.
   */
  sandbox?: boolean;
  className?: string;
}

// Mirrors `toScalapayAmount` in app/lib/scalapay.ts: round to cents first to
// avoid binary float drift before formatting (e.g. 19.669999999999998 ->
// "19.67" consistently). Reimplemented locally rather than importing that
// module, to keep its ScalapayClient/webhook-HMAC code out of the client
// bundle for a component that only needs this one formatting rule.
function formatAmount(amount: number): string {
  const cents = Math.round(amount * 100);
  return (cents / 100).toFixed(2);
}

export function ScalapayWidget({
  merchantToken,
  amount,
  minAmount,
  maxAmount,
  type,
  channel,
  sandbox,
  className,
}: ScalapayWidgetProps) {
  const eligible =
    Number.isFinite(amount) && amount >= minAmount && amount <= maxAmount;

  // Must run before the early returns below — hooks cannot be conditional.
  useScalapayLoaderScript(Boolean(merchantToken) && eligible);

  if (!merchantToken || !eligible) return null;

  return (
    <div className={className}>
      <scalapay-widget
        merchant-token={merchantToken}
        amount={formatAmount(amount)}
        min-amount={formatAmount(minAmount)}
        max-amount={formatAmount(maxAmount)}
        type={type}
        channel={channel}
        locale="it"
        environment={sandbox ? "integration" : undefined}
      />
    </div>
  );
}
